/**
 * PremiumDarkAtmosphere — Reusable atmospheric layer for dark sections.
 *
 * Reproduces the signature "Premium Dark Dossier" cinematic lighting :
 *  - Multiple radial orange/red glows positioned strategically
 *  - Subtle vignette
 *  - Optional grain noise
 *
 * Usage:
 *   <section className="relative ... " style={{ background: '...' }}>
 *     <PremiumDarkAtmosphere />
 *     <div className="relative">...content...</div>
 *   </section>
 *
 * Variants:
 *  - 'default' (orange + red) — for emotional/storytelling sections
 *  - 'orange'  (orange only)  — for CTA/pricing/scarcity
 *  - 'cold'    (cyan + violet) — for the WorldPresenceMap or tech sections
 */
const VARIANTS = {
  default: [
    { x: '15%', y: '20%', size: 600, color: 'rgba(240,90,26,0.30)' },
    { x: '85%', y: '15%', size: 500, color: 'rgba(240,90,26,0.18)' },
    { x: '50%', y: '50%', size: 700, color: 'rgba(239,68,68,0.14)' },
    { x: '20%', y: '80%', size: 500, color: 'rgba(240,90,26,0.20)' },
    { x: '90%', y: '85%', size: 450, color: 'rgba(239,68,68,0.16)' },
  ],
  orange: [
    { x: '50%', y: '30%', size: 700, color: 'rgba(240,90,26,0.35)' },
    { x: '20%', y: '70%', size: 550, color: 'rgba(240,90,26,0.22)' },
    { x: '80%', y: '75%', size: 500, color: 'rgba(255,140,60,0.18)' },
  ],
  cold: [
    { x: '20%', y: '30%', size: 600, color: 'rgba(6,182,212,0.18)' },
    { x: '80%', y: '70%', size: 600, color: 'rgba(139,92,246,0.18)' },
  ],
};

const PremiumDarkAtmosphere = ({ variant = 'default', dots = true, vignette = true }) => {
  const glows = VARIANTS[variant] || VARIANTS.default;

  return (
    <>
      {/* Layered radial glows */}
      {glows.map((g, i) => (
        <div
          key={i}
          className="absolute rounded-full blur-3xl pointer-events-none"
          style={{
            top: `${g.y}`,
            left: `${g.x}`,
            transform: 'translate(-50%, -50%)',
            width: `${g.size}px`,
            height: `${g.size}px`,
            background: `radial-gradient(circle, ${g.color} 0%, transparent 65%)`,
          }}
          aria-hidden="true"
        />
      ))}

      {/* Vignette edge (subtle dark gradient toward corners) */}
      {vignette && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.55) 100%)',
          }}
          aria-hidden="true"
        />
      )}

      {/* Subtle dot pattern */}
      {dots && (
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default PremiumDarkAtmosphere;
